import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dates';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Staff.css';

const emptyStaffForm = () => ({
  name: '',
  phone: '',
  role_title: '',
  monthly_salary: '',
  join_date: new Date().toISOString().slice(0, 10),
  status: 'active',
  notes: ''
});

const emptyAttendanceForm = () => ({
  attendance_date: new Date().toISOString().slice(0, 10),
  status: 'present',
  check_in: '',
  check_out: '',
  notes: ''
});

const emptyAdvanceForm = () => ({
  advance_date: new Date().toISOString().slice(0, 10),
  amount: '',
  notes: ''
});

const Staff = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStaffForm());
  const [selectedId, setSelectedId] = useState(null);
  const [detailTab, setDetailTab] = useState('attendance');
  const [attendance, setAttendance] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [attForm, setAttForm] = useState(emptyAttendanceForm());
  const [advForm, setAdvForm] = useState(emptyAdvanceForm());
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();

  const selected = useMemo(
    () => staffList.find((s) => s.id === selectedId) || null,
    [staffList, selectedId]
  );

  const loadStaff = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/staff');
      setStaffList(res.data.staff || []);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const loadDetail = useCallback(async (staffId) => {
    if (!staffId) return;
    const from = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    try {
      const [attRes, advRes, sumRes] = await Promise.all([
        axios.get(`/api/staff/${staffId}/attendance`, { params: { from, to } }),
        axios.get(`/api/staff/${staffId}/advances`),
        axios.get(`/api/staff/${staffId}/salary-summary`, { params: { year, month } })
      ]);
      setAttendance(attRes.data.attendance || []);
      setAdvances(advRes.data.advances || []);
      setSummary(sumRes.data.summary || null);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load staff details');
    }
  }, [year, month]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyStaffForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      phone: row.phone || '',
      role_title: row.role_title || '',
      monthly_salary: String(row.monthly_salary ?? ''),
      join_date: row.join_date ? String(row.join_date).slice(0, 10) : '',
      status: row.status || 'active',
      notes: row.notes || ''
    });
    setShowModal(true);
  };

  const saveStaff = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_salary: parseFloat(form.monthly_salary) || 0
      };
      if (editing) {
        await axios.put(`/api/staff/${editing.id}`, payload);
      } else {
        const res = await axios.post('/api/staff', payload);
        setSelectedId(res.data.staff?.id || null);
      }
      setShowModal(false);
      await loadStaff();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save staff');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (row) => {
    if (!window.confirm(`Deactivate ${row.name}?`)) return;
    try {
      await axios.delete(`/api/staff/${row.id}`);
      if (selectedId === row.id) setSelectedId(null);
      await loadStaff();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to deactivate');
    }
  };

  const saveAttendance = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    try {
      await axios.post(`/api/staff/${selectedId}/attendance`, {
        ...attForm,
        check_in: attForm.check_in || null,
        check_out: attForm.check_out || null
      });
      setAttForm(emptyAttendanceForm());
      await loadDetail(selectedId);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save attendance');
    }
  };

  const deleteAttendance = async (row) => {
    if (!window.confirm('Delete this attendance entry?')) return;
    try {
      await axios.delete(`/api/staff/${selectedId}/attendance/${row.id}`);
      await loadDetail(selectedId);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete');
    }
  };

  const saveAdvance = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    try {
      await axios.post(`/api/staff/${selectedId}/advances`, {
        ...advForm,
        amount: parseFloat(advForm.amount)
      });
      setAdvForm(emptyAdvanceForm());
      await loadDetail(selectedId);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save advance');
    }
  };

  const settleAdvance = async (row) => {
    try {
      await axios.put(`/api/staff/${selectedId}/advances/${row.id}`, {
        status: 'settled',
        settled_period_ym: `${year}-${String(month).padStart(2, '0')}`
      });
      await loadDetail(selectedId);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to settle advance');
    }
  };

  const recordSalary = async () => {
    if (!selectedId || !summary) return;
    if (summary.payment) {
      alert('Salary already recorded for this month');
      return;
    }
    if (!window.confirm(
      `Record salary payment of ${formatCurrency(summary.net_suggested)} for ${summary.period_ym}?` +
      (summary.open_advances_total > 0 ? `\nOpen advances ${formatCurrency(summary.open_advances_total)} will be settled.` : '')
    )) return;
    try {
      await axios.post(`/api/staff/${selectedId}/salary-payments`, {
        year,
        month,
        settle_advances: true
      });
      await loadDetail(selectedId);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to record salary');
    }
  };

  if (loading && staffList.length === 0) {
    return (
      <div className="page-container">
        <div className="container">Loading staff…</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container staff-page">
        <div className="page-header">
          <div>
            <h1>Staff Management</h1>
            <p className="staff-subtitle">Track salary, attendance, and advances for store staff.</p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            Add Staff
          </button>
        </div>

        <div className="staff-layout">
          <div className="card staff-list-card">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffList.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center' }}>No staff yet</td>
                  </tr>
                ) : (
                  staffList.map((row) => (
                    <tr
                      key={row.id}
                      className={selectedId === row.id ? 'staff-row-active' : ''}
                      onClick={() => setSelectedId(row.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <strong>{row.name}</strong>
                        {row.phone ? <div className="staff-muted">{row.phone}</div> : null}
                      </td>
                      <td>{row.role_title || '—'}</td>
                      <td>{formatCurrency(row.monthly_salary)}</td>
                      <td>
                        <span className={`status-badge status-${row.status}`}>{row.status}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <KebabMenu
                          menuId={`staff-${row.id}`}
                          openId={openActionMenu}
                          setOpenId={setOpenActionMenu}
                          items={[
                            { label: 'Edit', icon: MENU_ICONS.edit, onClick: () => openEdit(row) },
                            row.status !== 'inactive' && {
                              label: 'Deactivate',
                              icon: MENU_ICONS.trash,
                              danger: true,
                              onClick: () => deactivate(row)
                            }
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="card staff-detail-card">
            {!selected ? (
              <div className="staff-empty-detail">Select a staff member to manage attendance, advances, and salary.</div>
            ) : (
              <>
                <div className="staff-detail-header">
                  <div>
                    <h2>{selected.name}</h2>
                    <div className="staff-muted">
                      {selected.role_title || 'Staff'} · {formatCurrency(selected.monthly_salary)} / month
                    </div>
                  </div>
                  <div className="staff-period">
                    <label>
                      Month
                      <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))}>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(2000, i, 1).toLocaleString('en', { month: 'short' })}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Year
                      <input
                        type="number"
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
                        style={{ width: 90 }}
                      />
                    </label>
                  </div>
                </div>

                <div className="staff-tabs">
                  {['attendance', 'advances', 'salary'].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      className={`staff-tab ${detailTab === tab ? 'active' : ''}`}
                      onClick={() => setDetailTab(tab)}
                    >
                      {tab === 'attendance' ? 'Attendance' : tab === 'advances' ? 'Advances' : 'Salary'}
                    </button>
                  ))}
                </div>

                {detailTab === 'attendance' && (
                  <div className="staff-panel">
                    <form className="staff-inline-form" onSubmit={saveAttendance}>
                      <DateInput
                        value={attForm.attendance_date}
                        onChange={(e) => setAttForm({ ...attForm, attendance_date: e.target.value })}
                      />
                      <select
                        value={attForm.status}
                        onChange={(e) => setAttForm({ ...attForm, status: e.target.value })}
                      >
                        <option value="present">Present</option>
                        <option value="half_day">Half-day</option>
                        <option value="absent">Absent</option>
                      </select>
                      <input
                        type="time"
                        value={attForm.check_in}
                        onChange={(e) => setAttForm({ ...attForm, check_in: e.target.value })}
                        title="Check-in"
                      />
                      <input
                        type="time"
                        value={attForm.check_out}
                        onChange={(e) => setAttForm({ ...attForm, check_out: e.target.value })}
                        title="Check-out"
                      />
                      <button type="submit" className="btn btn-primary btn-sm">Save day</button>
                    </form>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>In</th>
                          <th>Out</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendance.length === 0 ? (
                          <tr><td colSpan="5" style={{ textAlign: 'center' }}>No attendance this month</td></tr>
                        ) : (
                          attendance.map((row) => (
                            <tr key={row.id}>
                              <td>{formatDate(row.attendance_date)}</td>
                              <td>{String(row.status).replace('_', ' ')}</td>
                              <td>{row.check_in || '—'}</td>
                              <td>{row.check_out || '—'}</td>
                              <td>
                                <KebabMenu
                                  menuId={`attendance-${row.id}`}
                                  openId={openActionMenu}
                                  setOpenId={setOpenActionMenu}
                                  items={[
                                    {
                                      label: 'Delete',
                                      icon: MENU_ICONS.trash,
                                      danger: true,
                                      onClick: () => deleteAttendance(row)
                                    }
                                  ]}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {detailTab === 'advances' && (
                  <div className="staff-panel">
                    <form className="staff-inline-form" onSubmit={saveAdvance}>
                      <DateInput
                        value={advForm.advance_date}
                        onChange={(e) => setAdvForm({ ...advForm, advance_date: e.target.value })}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Amount"
                        value={advForm.amount}
                        onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Notes"
                        value={advForm.notes}
                        onChange={(e) => setAdvForm({ ...advForm, notes: e.target.value })}
                      />
                      <button type="submit" className="btn btn-primary btn-sm">Add advance</button>
                    </form>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Notes</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {advances.length === 0 ? (
                          <tr><td colSpan="5" style={{ textAlign: 'center' }}>No advances</td></tr>
                        ) : (
                          advances.map((row) => (
                            <tr key={row.id}>
                              <td>{formatDate(row.advance_date)}</td>
                              <td>{formatCurrency(row.amount)}</td>
                              <td>
                                <span className={`status-badge status-${row.status}`}>{row.status}</span>
                                {row.settled_period_ym ? ` (${row.settled_period_ym})` : ''}
                              </td>
                              <td>{row.notes || '—'}</td>
                              <td>
                                <KebabMenu
                                  menuId={`advance-${row.id}`}
                                  openId={openActionMenu}
                                  setOpenId={setOpenActionMenu}
                                  items={[
                                    row.status === 'open' && {
                                      label: 'Settle',
                                      icon: MENU_ICONS.settle,
                                      onClick: () => settleAdvance(row)
                                    }
                                  ]}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {detailTab === 'salary' && summary && (
                  <div className="staff-panel">
                    <div className="staff-summary-grid">
                      <div className="staff-summary-item">
                        <span>Base salary</span>
                        <strong>{formatCurrency(summary.base_salary)}</strong>
                      </div>
                      <div className="staff-summary-item">
                        <span>Present / Half / Absent</span>
                        <strong>{summary.present_days} / {summary.half_days} / {summary.absent_days}</strong>
                      </div>
                      <div className="staff-summary-item">
                        <span>Days counted</span>
                        <strong>{summary.days_present} / {summary.days_in_month}</strong>
                      </div>
                      <div className="staff-summary-item">
                        <span>Suggested gross</span>
                        <strong>{formatCurrency(summary.suggested_gross)}</strong>
                      </div>
                      <div className="staff-summary-item">
                        <span>Open advances</span>
                        <strong>{formatCurrency(summary.open_advances_total)}</strong>
                      </div>
                      <div className="staff-summary-item highlight">
                        <span>Net suggested</span>
                        <strong>{formatCurrency(summary.net_suggested)}</strong>
                      </div>
                    </div>
                    {summary.payment ? (
                      <div className="staff-paid-banner">
                        Paid {formatCurrency(summary.payment.net_paid)} on {formatDate(summary.payment.paid_date)}
                        {summary.payment.advance_deducted > 0
                          ? ` (advances deducted ${formatCurrency(summary.payment.advance_deducted)})`
                          : ''}
                      </div>
                    ) : (
                      <button type="button" className="btn btn-primary" onClick={recordSalary}>
                        Record salary payment
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editing ? 'Edit Staff' : 'Add Staff'}</h2>
                <button type="button" className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={saveStaff}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Role / title</label>
                  <input
                    value={form.role_title}
                    onChange={(e) => setForm({ ...form, role_title: e.target.value })}
                    placeholder="Sales, Tailor…"
                  />
                </div>
                <div className="form-group">
                  <label>Monthly salary *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.monthly_salary}
                    onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Join date</label>
                  <DateInput
                    value={form.join_date}
                    onChange={(e) => setForm({ ...form, join_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    rows="2"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Staff;
